// src/context/ChatbotContext.js
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { clearActivityCache } from '../services/chatbotActivity';
import logger from '../utils/logger';
import { getApiBaseUrl } from '../config/apiConfig';
import { authenticatedFetch } from '../utils/apiUtils';
import { useUser } from './UserContext';

const ChatbotContext = createContext();

export const useChatbotContext = () => useContext(ChatbotContext);

export const ChatbotProvider = ({ children }) => {
  const { user } = useUser();
  const [chatbots, setChatbots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingChatbot, setEditingChatbot] = useState(null); // Tracks chatbot being edited

  /**
   * Fetch chatbots for the authenticated user
   * @param {string} userId - The authenticated user's ID
   */
  const fetchChatbots = async (userId) => {
    try {
      // Fetch chatbots without any encryption/decryption
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/user/chatbots?userId=${userId}`);
      if (!response.ok) throw new Error('Failed to fetch chatbots');
      const data = await response.json();
      logger.log('Chatbots fetched:', data.chatbots);

      // Set chatbots without any special processing
      setChatbots(data.chatbots || []);
    } catch (error) {
      logger.error('Error fetching chatbots:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refresh the chatbot list when needed
   */
  const refreshChatbots = useCallback(async () => {
    setLoading(true);

    // Only clear activity cache on manual refreshes to avoid unnecessary API calls
    clearActivityCache();

    if (user) {
      await fetchChatbots(user.uid);
    } else {
      logger.error('User not authenticated');
      setLoading(false);
    }
  }, [user]);

  /**
   * Fetch chatbot configuration for editing
   * @param {string} chatbotId - The ID of the chatbot to edit
   * @returns {object} - The chatbot configuration
   */
  const fetchChatbotConfig = async (chatbotId) => {
    try {
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/chatbot?chatbotId=${chatbotId}`);
      if (!response.ok) throw new Error('Failed to fetch chatbot configuration');
      const data = await response.json();

      // Return config as-is
      return data.config;
    } catch (error) {
      logger.error('Error fetching chatbot configuration:', error);
      return null;
    }
  };

  /**
   * Reset chatbot configuration to default for new chatbot
   * @returns {object} - The default chatbot configuration
   */
  const resetToDefaultConfig = () => ({
    assistantName: 'Bella',
    messagePrompt: "Type your message and hit 'Enter'",
    introMessageText: "Hello! How can I help you today?",
    botTagline: "Your friendly AI assistant",
    headerColor: '#292929',
    chatBackgroundColor: '#FFFFFF',
    botMessageBoxColor: '#F7F7F7',
    botMessageTextColor: '#292929',
    userMessageBoxColor: '#292929',
    userMessageTextColor: '#F7F7F7',
    openAIVectorStoreID: '',
    openAIAssistantID: '',
    openAIAPIKey: '',
    requiresCustomAPIKey: false,
    uploadedFiles: [],
    chatbotRolePrompt: `You are a Professional Customer Support Executive. Your role is to provide clear, concise, and accurate information to customers, following these key principles:

1. SCOPE:
   - Answer only questions related to our services
   - For anything outside this scope, respond with: "I'm here to help with our services. For other topics you might need different support."
   - Address customers respectfully and warmly.
   - Keep responses brief and clear, limited to 3-4 sentences (unless detailed explanation needed).
   - Occasionally include a relevant link to the website in your response when possible.

2. GENERAL GUIDELINES:
   - Always reply as the customer support representative, Use "our" instead of "i" and other first person references
   - Maintain a Professional tone.
   - Provide efficient, accurate support while ensuring customers feel valued and understood.
   - ALWAYS search all files comprehensively and sources before answering ANY question.
   - If any resources can be found in the website/blog page, guide users to read more or attain the required details from that page by providing a markdown source to the page
   - Keep your answers within 100 words, If the answer requires a lengthy answer over 100 words, summarize the point concerned in short around 100 words and refer to the relevant page for the webpage or the blog article where it can be read more about
   - Always showcase that your response stem from your own knowledge and never mention any files/resources as this may breach personal data
   - Always try to include a relevant link to the website in your responses.
   - When user requests for the email id, Always provide them with contact details provided in the files uploaded.
   - NEVER mention documents/resources or provide any hint that your answers have been fetched from any sort of knowledge base

3. WEBSITE NAVIGATION:
   - Actively guide users to relevant pages on the website whenever possible.
   - Use only specific URLs directly from the website context provided.
   - Format links with descriptive text in markdown that accurately reflects the page content.
   - Do not create or invent new URLs. Only use links you are certain exist on the website.

4. PRODUCT INFORMATION:
   - Guide user to the relevant Product/Service page if available or to the parent Shop/Services page where users can choose from.
   - Do not provide users with made-up information or Product/Service names. Only provide information about products/services that are explicitly listed on the website. If you don't find a particular product in your knowledge base or on the website, inform the customer that the Product/Service is not available and guide them to the overview page.
   - Before mentioning any Product/Service, verify its existence by checking the listings on the website. Do not assume or infer details that are not explicitly stated.

5. UNCERTAINTY HANDLING:
   - If unsure or if confidence is low, prompt the customer to connect with a representative by saying: "For more detailed information on this topic, I recommend checking our website. If you need further assistance, please contact us."

6. WRITING STYLE:
   - Plain English, active voice. Aim for Flesch ≥ 80.
   - Keep responses direct and free of fluff, buzzwords, adverbs, or overt sales tone.
   - Sound like a human: supportive, grounded, empathetic, not robotic.
   - DO NOT USE em-dashes in your response
   - ALWAYS USE markdown formatting for your response`,
    defaultLanguage: 'en',
    responseDelay: 0,
    openChatOnLoad: false,
    chatOpenDelay: 0,
    showPoweredBy: true,
    showLinkPreviews: true,
    chatbotPosition: 'right',
    announcement: {
      text: '',
      enabled: false,
      style: 'info'
    },
    headerStyle: 'compact',
    botIconUrl: '',
    // Proactive Bubble Settings
    proactiveBubbleEnabled: true,
    proactiveBubbleDelay: 5,
    proactiveBubbleMode: 'ai',
    proactiveBubbleDefaultMessage: 'Have a question? Ask me anything!',
  });

  /**
   * Set chatbot for editing when user clicks "Edit"
   * @param {Object} chatbot - The chatbot data to edit
   */
  const onEditChatbot = async (chatbotId) => {
    const fetchedConfig = await fetchChatbotConfig(chatbotId);
    if (fetchedConfig) {
      setEditingChatbot(fetchedConfig);
    }
  };

  /**
   * Reset chatbot state for creating a new chatbot
   */
  const onAddNewChatbot = () => {
    const defaultConfig = resetToDefaultConfig();
    setEditingChatbot(defaultConfig);
  };

  useEffect(() => {
    if (user) {
      fetchChatbots(user.uid);
    } else {
      setChatbots([]);
      setLoading(false);
    }
  }, [user]);

  return (
    <ChatbotContext.Provider
      value={{
        chatbots,
        setChatbots,
        loading,
        refreshChatbots,
        editingChatbot,
        setEditingChatbot,
        onEditChatbot,
        onAddNewChatbot,
        fetchChatbotConfig,
        resetToDefaultConfig
      }}
    >
      {children}
    </ChatbotContext.Provider>
  );
};
